import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Briefcase, Plus } from "lucide-react"
import { type FormEvent, useEffect, useState } from "react"
import { useSearchParams } from "react-router"
import { api, ErrorApi } from "@/api/cliente"
import {
  type Borrador,
  borradorDe,
  type Cargo,
  cuerpoDe,
  type Departamento,
  type Descriptivo,
  estaSucio,
} from "@/componentes/cargos/cargo"
import { DescriptivoDeCargo } from "@/componentes/cargos/DescriptivoDeCargo"
import { documentoDeCargos } from "@/componentes/cargos/exportarCargos"
import { Exportar } from "@/componentes/exportar/Exportar"
import { KpiCargos } from "@/componentes/kpi/KpiPlanificacion"
import { Modal } from "@/componentes/ui/Modal"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/**
 * El catálogo de cargos: qué puestos hay, en qué área está cada uno y qué se espera de él.
 *
 * ⚠ **Vive en Planificación estratégica, con permiso propio.** Fue una pestaña de
 * «Personas» —se tocaba al rellenar una ficha y no encontrar el puesto— y por eso
 * pedía los permisos del expediente: para saber qué puestos tiene la agencia había que
 * poder ver la cédula de todo el mundo. Describir la estructura no es dato personal.
 *
 * ⚠ **El cargo dejó de ser texto libre en la ficha.** Con texto libre, «Tramitador»,
 * «tramitador» y «Tramitador de aduana» eran tres puestos para la base y el mismo
 * para las personas: no se podía contar cuántos hay de cada uno, ni corregir el
 * nombre de uno sin repasar las fichas a mano.
 *
 * ⚠ **Hay DOS orígenes de funciones, y están separados a propósito.** Las escritas a
 * mano y las que salen de los pasos de los procesos. Ver `FuncionesDelFlujo`.
 *
 * ⚠ **Sin `cargo:administrar` se consulta y no se edita.** Quien sólo lo ve —el
 * operativo, que lo necesita para leer el organigrama— no recibe ni el botón de crear
 * ni los campos.
 *
 * `?cargo=<id>` abre ese cargo: es a donde llevan los puestos de «Departamentos».
 */
export function CatalogoCargos() {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeEditar = tienePermiso(usuario, "cargo:administrar")
  const [parametros] = useSearchParams()
  const [elegido, setElegido] = useState<number | null>(
    () => Number(parametros.get("cargo")) || null,
  )
  const [creando, setCreando] = useState(false)
  const [nombreNuevo, setNombreNuevo] = useState("")
  // El borrador local: los campos se editan varios y se guardan de una vez.
  const [borrador, setBorrador] = useState<Borrador | null>(null)

  const cargos = useQuery({
    queryKey: ["cargos"],
    queryFn: () => api.get<Cargo[]>("/api/v1/cargos"),
  })

  // El área se elige de esta lista, así que sólo la pide quien puede cambiarla.
  const departamentos = useQuery({
    queryKey: ["departamentos"],
    queryFn: () => api.get<Departamento[]>("/api/v1/departamentos"),
    enabled: puedeEditar,
  })

  const cargo = cargos.data?.find((c) => c.id === elegido) ?? cargos.data?.[0]

  // Al cambiar de cargo se recarga su borrador. Sin esto, las funciones del anterior
  // se quedarían puestas sobre el nuevo: se vería el descriptivo de otro, y se
  // guardaría también.
  useEffect(() => {
    if (cargo !== undefined) setBorrador(borradorDe(cargo))
  }, [cargo])

  /**
   * El descriptivo, que **se calcula y no se guarda**.
   *
   * Guardarlo obligaría a regenerarlo cada vez que alguien toca un paso, y el día
   * que se olvidara una regeneración el contrato diría funciones que ya no son.
   */
  const descriptivo = useQuery({
    queryKey: ["descriptivo", cargo?.id],
    queryFn: () => api.get<Descriptivo>(`/api/v1/cargos/${cargo?.id}/descriptivo`),
    enabled: cargo !== undefined,
  })

  const alCambiar = () => {
    clienteQuery.invalidateQueries({ queryKey: ["cargos"] })
    // Cuántos puestos tiene cada área cambia al mover uno de sitio.
    clienteQuery.invalidateQueries({ queryKey: ["departamentos"] })
  }

  const guardar = useMutation({
    mutationFn: ({ id, datos }: { id: number; datos: Borrador }) =>
      api.patch(`/api/v1/cargos/${id}`, cuerpoDe(datos)),
    onSuccess: alCambiar,
  })

  const crear = useMutation({
    mutationFn: () => api.post<Cargo>("/api/v1/cargos", { nombre: nombreNuevo }),
    onSuccess: (nuevo) => {
      alCambiar()
      setCreando(false)
      setNombreNuevo("")
      setElegido(nuevo.id)
    },
  })

  const retirar = useMutation({
    mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
      api.patch(`/api/v1/cargos/${id}`, { activo }),
    onSuccess: alCambiar,
  })

  if (cargos.isPending) {
    return (
      <p role="status" className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }

  if (cargos.isError) {
    return (
      <p role="alert" className="py-12 text-center text-sm" style={{ color: "var(--destructive)" }}>
        {cargos.error instanceof ErrorApi ? cargos.error.message : "No se pudo cargar el catálogo"}
      </p>
    )
  }

  const sucio = cargo !== undefined && borrador !== null && estaSucio(borrador, cargo)

  function enviarNuevo(evento: FormEvent) {
    evento.preventDefault()
    crear.mutate()
  }

  return (
    // Los indicadores van encima de la rejilla: esta pestaña no tiene barra de acciones arriba.
    <div>
      <KpiCargos />
      <div className="grid gap-4 lg:grid-cols-[18rem_1fr]">
        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <h2 className="rotulo flex-1">Cargos</h2>
            {cargos.data.length > 0 && (
              <Exportar
                titulo="Catálogo de cargos"
                documento={() => documentoDeCargos(cargos.data)}
              />
            )}
            {puedeEditar && (
              <button
                type="button"
                onClick={() => setCreando(true)}
                aria-haspopup="dialog"
                className="boton-secundario h-8 px-2 text-xs"
              >
                <Plus size={14} aria-hidden={true} />
                Nuevo
              </button>
            )}
          </div>

          {cargos.data.length === 0 ? (
            <div
              className="flex flex-col items-center gap-3 superficie py-12"
              style={{ color: "var(--fg-muted)" }}
            >
              <Briefcase size={24} aria-hidden={true} />
              <p className="px-4 text-center text-sm">
                {puedeEditar
                  ? "Todavía no hay cargos. Créalos aquí y aparecerán en las fichas, el organigrama y los procesos."
                  : "Todavía no hay cargos en el catálogo."}
              </p>
            </div>
          ) : (
            <ul className="superficie overflow-hidden">
              {cargos.data.map((c, i) => (
                <li
                  key={c.id}
                  style={i === 0 ? undefined : { borderTop: "1px solid var(--cristal-filo)" }}
                >
                  <button
                    type="button"
                    onClick={() => setElegido(c.id)}
                    data-testid={`cargo-${c.id}`}
                    aria-current={c.id === cargo?.id}
                    className="block w-full px-3 py-2 text-left text-sm toque-cristal"
                    style={
                      c.id === cargo?.id
                        ? { backgroundColor: "var(--primary-suave)", color: "var(--primary-texto)" }
                        : undefined
                    }
                  >
                    <span className="block font-medium" style={{ opacity: c.activo ? 1 : 0.55 }}>
                      {c.nombre}
                      {!c.activo && " (retirado)"}
                    </span>
                    <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                      {c.departamento_nombre ?? "Sin departamento"} · {c.personas} persona
                      {c.personas === 1 ? "" : "s"} · {c.funciones.length} funcion
                      {c.funciones.length === 1 ? "" : "es"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {cargo !== undefined && borrador !== null && (
          <DescriptivoDeCargo
            cargo={cargo}
            descriptivo={descriptivo.data}
            borrador={borrador}
            setBorrador={setBorrador}
            departamentos={departamentos.data}
            cargos={cargos.data}
            soloLectura={!puedeEditar}
            sucio={sucio}
            guardando={guardar.isPending}
            error={guardar.error ?? retirar.error}
            onGuardar={() => guardar.mutate({ id: cargo.id, datos: borrador })}
            onRetirar={(activo) => retirar.mutate({ id: cargo.id, activo })}
          />
        )}

        <Modal
          abierto={creando}
          titulo="Nuevo cargo"
          descripcion="Nace sin área ni descriptivo: se le ponen después, al elegirlo en la lista."
          ancho="sm"
          onCerrar={() => setCreando(false)}
        >
          <form onSubmit={enviarNuevo} aria-label="Nuevo cargo">
            <label htmlFor="cargo-nombre" className="mb-1 block text-xs font-medium uppercase">
              Nombre del puesto
            </label>
            <input
              id="cargo-nombre"
              required
              minLength={2}
              className="campo"
              value={nombreNuevo}
              onChange={(e) => setNombreNuevo(e.target.value)}
            />
            {crear.error && (
              <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
                {crear.error instanceof ErrorApi ? crear.error.message : "No se pudo crear"}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCreando(false)}
                className="boton-secundario h-9 px-3 text-sm"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={crear.isPending}
                className="boton-primario h-9 px-3 text-sm"
              >
                Crear cargo
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </div>
  )
}
